import React, { createContext, useContext, useState, useCallback } from 'react';
import { MAX_BATCH_SIZE } from './utils';

const BatchContext = createContext(null);

export const BATCH_TYPES = {
    USEFUL_TO_NOT_USEFUL: 'usefulToNotUseful',
    NOT_USEFUL_TO_USEFUL: 'notUsefulToUseful',
};

/**
 * @typedef {Object} BatchItem
 * @property {string} line trimmed line text
 * @property {string} id
 */

/**
 * @typedef {Object} BatchContextValue
 * @property {BatchItem[]} usefulToNotUseful
 * @property {BatchItem[]} notUsefulToUseful
 * @property {(line: string, batchType: string) => boolean} addToBatch
 * @property {(id: string, batchType: string) => void} removeFromBatch
 * @property {(batchType: string) => void} clearBatch
 * @property {(batchType: string) => boolean} isBatchFull
 * @property {(batchType: string) => BatchItem[]} getBatch
 */

export const BatchProvider = ({ children }) => {
    const [usefulToNotUseful, setUsefulToNotUseful] = useState([]);
    const [notUsefulToUseful, setNotUsefulToUseful] = useState([]);

    const getBatch = useCallback((batchType) => {
        if (batchType === BATCH_TYPES.USEFUL_TO_NOT_USEFUL) {
            return usefulToNotUseful;
        }
        return notUsefulToUseful;
    }, [usefulToNotUseful, notUsefulToUseful]);

    const isBatchFull = useCallback((batchType) => {
        const batch = getBatch(batchType);
        return batch.length >= MAX_BATCH_SIZE;
    }, [getBatch]);

    const addToBatch = useCallback((line, batchType) => {
        const trimmedLine = line.trim();
        const normalizedLine = trimmedLine.toLowerCase();
        const newItem = {
            line: trimmedLine,
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };

        if (batchType === BATCH_TYPES.USEFUL_TO_NOT_USEFUL) {
            if (usefulToNotUseful.length >= MAX_BATCH_SIZE) {
                return false;
            }
            
            // dups - use normalized comparison
            if (usefulToNotUseful.some(item => item.line.trim().toLowerCase() === normalizedLine)) {
                return false;
            }
            setUsefulToNotUseful(prev => [...prev, newItem]);
        } else {
            if (notUsefulToUseful.length >= MAX_BATCH_SIZE) {
                return false;
            }

            // dups - use normalized comparison
            if (notUsefulToUseful.some(item => item.line.trim().toLowerCase() === normalizedLine)) {
                return false;
            }
            setNotUsefulToUseful(prev => [...prev, newItem]);
        }
        return true;
    }, [usefulToNotUseful, notUsefulToUseful]);

    const removeFromBatch = useCallback((id, batchType) => {
        if (batchType === BATCH_TYPES.USEFUL_TO_NOT_USEFUL) {
            setUsefulToNotUseful(prev => prev.filter(item => item.id !== id));
        } else {
            setNotUsefulToUseful(prev => prev.filter(item => item.id !== id));
        }
    }, []);

    const clearBatch = useCallback(() => {
        setUsefulToNotUseful([]);
        setNotUsefulToUseful([]);
    }, []);

    const addMultipleToBatch = useCallback((items, batchType) => {
        const createItem = line => ({
            line: line.trim(),
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        });

        if (batchType === BATCH_TYPES.USEFUL_TO_NOT_USEFUL) {
            setUsefulToNotUseful((prev) => {
                const existingLines = new Set(prev.map(item => item.line.trim().toLowerCase()));
                const newItems = items
                    .filter(line => !existingLines.has(line.trim().toLowerCase()))
                    .map(createItem);
                return [...prev, ...newItems].slice(0, MAX_BATCH_SIZE);
            });
        } else {
            setNotUsefulToUseful((prev) => {
                const existingLines = new Set(prev.map(item => item.line.trim().toLowerCase()));
                const newItems = items
                    .filter(line => !existingLines.has(line.trim().toLowerCase()))
                    .map(createItem);
                return [...prev, ...newItems].slice(0, MAX_BATCH_SIZE);
            });
        }
    }, []);

    const value = {
        data: {
            usefulToNotUseful,
            notUsefulToUseful,    
        },
        addToBatch,
        addMultipleToBatch,
        removeFromBatch,
        clearBatch,
        isBatchFull,
        getBatch,
    };

    return (
        <BatchContext.Provider value={value}>
            {children}
        </BatchContext.Provider>
    );
};

export const useBatch = () => {
    const context = useContext(BatchContext);
    if (!context) {
        throw new Error('useBatch must be used within a BatchProvider');
    }
    return context;
};

export default BatchContext;




// WEBPACK FOOTER //
// ./app/components/Letters/Single/Gazetter/Content/BatchContext.jsx